import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Card, Spinner, Badge } from 'react-bootstrap';
import { FaVideo, FaPlay, FaDownload, FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import './styles.css';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';

const ModalVideos = ({
  show,
  onHide,
  empreendimentoNome,
  codigoGrupoEmpresa,
}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [videoAtual, setVideoAtual] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const categoria = 'video';

  useEffect(() => {
    if (show && codigoGrupoEmpresa) {
      carregarVideos();
    }
  }, [show, codigoGrupoEmpresa]);

  const carregarVideos = async () => {
    setLoading(true);
    try {
      const data = await api.SismacListar(categoria, codigoGrupoEmpresa);
      setVideos(data.arquivos || []);
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
      toast.error(
        (error && error.message) || 'Erro ao carregar vídeos. Tente novamente.'
      );
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePararVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (videoAtual && videoAtual.url) {
      window.URL.revokeObjectURL(videoAtual.url);
    }
    setVideoAtual(null);
  };

  const handleReproduzirVideo = async video => {
    try {
      if (videoAtual) {
        handlePararVideo();
      }

      toast.success(`Abrindo vídeo: ${video.nome}`);
      setDownloading({ [video.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivo(
        categoria,
        codigoGrupoEmpresa,
        video.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      setVideoAtual({ url: url, nome: video.nome });
      setDownloading({ [video.nome_completo]: false });
    } catch (error) {
      console.error('Erro ao carregar vídeo:', error);
      toast.error('Erro ao carregar vídeo.');
      setDownloading(prev => ({ ...prev, [video.nome_completo]: false }));
    }
  };

 const handleUploadVideo = async event => {
   const files = event.target.files;
   if (!files || files.length === 0) return;

   setUploading(true);

   try {
     for (let i = 0; i < files.length; i++) {
       const file = files[i];

       if (!file.name.toLowerCase().endsWith('.mp4')) {
         toast.error('Apenas arquivos MP4 são permitidos!');
         continue;
       }

       const formData = new FormData();
       formData.append('file', file);

       await api.SismacUploadArquivo(categoria, codigoGrupoEmpresa, formData);

       toast.success(file.name + ' enviado com sucesso!');
     }

     await carregarVideos();
   } catch (error) {
     console.error('Erro ao fazer upload:', error);
     toast.error('Erro ao fazer upload do vídeo.');
   } finally {
     setUploading(false);
     if (fileInputRef.current) {
       fileInputRef.current.value = '';
     }
   }
 };

  const handleDownloadVideo = async video => {
    try {
      toast.success(`Download iniciado: ${video.nome}`);
      setDownloading({ [video.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivo(
        categoria,
        codigoGrupoEmpresa,
        video.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadData.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download concluído!');
      setDownloading({ [video.nome_completo]: false });
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do vídeo.');
      setDownloading({ [video.nome_completo]: false });
    }
  };

  const handleExcluirVideo = async video => {
    try {
      await api.SismacExcluirArquivo(
        categoria,
        codigoGrupoEmpresa,
        video.nome_completo
      );
      toast.success('Vídeo excluído com sucesso!');

      if (videoAtual && videoAtual.nome === video.nome) {
        handlePararVideo();
      }

      await carregarVideos();
    } catch (error) {
      console.error('Erro ao excluir vídeo:', error);
      toast.error('Erro ao excluir vídeo.');
    }
  };

  const handleClose = () => {
    handlePararVideo();
    setVideos([]);
    onHide();
  };

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      dialogClassName="sismac_modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ color: '#fff' }}>
          VÍDEOS - {empreendimentoNome}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: '#0747a6' }} />
            <p className="mt-2" style={{ color: '#0747a6' }}>
              Carregando vídeos...
            </p>
          </div>
        ) : (
          <>
            {videoAtual && (
              <div className="sismac_player">
                <div className="sismac_player-header">
                  <h6>
                    <FaPlay />
                    <span>Reproduzindo: {videoAtual.nome}</span>
                  </h6>
                  <FaTimes
                    className="sismac_player-close"
                    onClick={handlePararVideo}
                    title="Fechar player"
                  />
                </div>
                <div className="sismac_player-body sismac_player-body--video">
                  <video ref={videoRef} controls autoPlay src={videoAtual.url}>
                    Seu navegador não suporta o elemento de vídeo.
                  </video>
                </div>
              </div>
            )}

            <Card className="sismac_card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 d-flex align-items-center text-secondary">
                  <FaVideo className="mr-2" />
                  <span>Vídeos disponíveis</span>
                  {videos.length > 0 && (
                    <Badge bg="secondary" className="ms-2">
                      {videos.length}
                    </Badge>
                  )}
                </h6>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".mp4"
                    multiple
                    onChange={handleUploadVideo}
                  />
                  {AuthStorage.temCaracteristica(
                    caracteristicas.GerencialSismac
                  ) && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleFileInputClick}
                      disabled={uploading}
                    >
                      <FaPlus className="mr-1" />
                      {uploading ? 'Enviando...' : 'Adicionar vídeo'}
                    </Button>
                  )}
                </div>
              </Card.Header>

              <Card.Body>
                {videos.length === 0 ? (
                  <div className="sismac_empty">
                    <FaVideo size={40} className="sismac_empty-icon" />
                    <p className="sismac_empty-text">
                      Nenhum vídeo encontrado.
                    </p>
                  </div>
                ) : (
                  <div className="sismac_list">
                    {videos.map((video, index) => (
                      <div key={index} className="sismac_list-item">
                        <div className="sismac_list-item-content">
                          <FaVideo
                            className="sismac_list-item-icon sismac_list-item-icon--primary"
                            size={24}
                          />
                          <div className="sismac_list-item-info">
                            <span
                              className="sismac_list-item-title"
                              onClick={() => handleReproduzirVideo(video)}
                            >
                              <FaPlay size={12} />
                              {video.nome}
                            </span>
                            <div className="sismac_list-item-subtitle">
                              {video.tamanho_formatado} • {video.extensao}
                            </div>
                          </div>
                        </div>
                        <div className="sismac_list-item-actions">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleDownloadVideo(video)}
                          disabled={downloading[video.nome_completo] || downloading[video.nome_completo]}
                        >
                          <FaDownload className="mr-1" />
                          {downloading[video.nome_completo] ? 'Baixando...' : 'Download'}
                        </Button>
                          {AuthStorage.temCaracteristica(
                            caracteristicas.GerencialSismac
                          ) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleExcluirVideo(video)}
                          >
                            <FaTimes className="mr-1" />
                            Excluir
                          </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalVideos;
